/**
 * MUSKLE premium collection banner — text entrance + optional slide loop.
 * Scope: [data-muskle-ccb-animated] (creatine, eiwitten-proteine, …).
 * Slide loop runs only when [data-muskle-ccb-slide-loop] is present.
 * Optional [data-muskle-ccb-promo-start] / [data-muskle-ccb-promo-end] enforce
 * a client-side campaign window (Amino Acids) so cached Liquid cannot stick.
 * Sequence (loop): soft title/subtitle entrance → hold → title stack fade out → pause
 *         → image crossfade to slide2 → hold → crossfade to slide1
 *         → title stack fade in → repeat.
 * Respects prefers-reduced-motion (no loop). Campaign window still shows slide 2.
 */
(function () {
  "use strict";

  var SLIDE_HOLD_MS = 5500;
  var TITLE_FADE_MS = 500;
  var TITLE_PAUSE_MS = 220;
  var IMAGE_CROSSFADE_MS = 1000;
  var ENTRANCE_SETTLE_MS = 350;
  var ENTRANCE_TOTAL_MS = 1800;

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function wait(ms, signal) {
    return new Promise(function (resolve, reject) {
      if (signal && signal.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      var id = window.setTimeout(resolve, ms);
      if (signal) {
        signal.addEventListener(
          "abort",
          function () {
            window.clearTimeout(id);
            reject(new DOMException("Aborted", "AbortError"));
          },
          { once: true }
        );
      }
    });
  }

  function whenImagesReady(root) {
    var imgs = root.querySelectorAll(".muskle-ccb__img");
    var list = [];
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete && img.naturalWidth > 0) continue;
      list.push(
        new Promise(function (resolve) {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        })
      );
    }
    return Promise.all(list);
  }

  function setSlide(root, slideNum) {
    var slides = root.querySelectorAll("[data-muskle-ccb-slide]");
    for (var i = 0; i < slides.length; i++) {
      var el = slides[i];
      var n = el.getAttribute("data-muskle-ccb-slide");
      var active = n === String(slideNum);
      el.classList.toggle("muskle-ccb__slide--active", active);
      el.setAttribute("aria-hidden", active ? "false" : "true");
    }
    root.setAttribute("data-slide", String(slideNum));
    root.classList.toggle("muskle-ccb--on-slide-2", slideNum === 2);
  }

  function setTitleVisible(root, visible) {
    root.classList.toggle("muskle-ccb--title-hidden", !visible);
    var title = root.querySelector(".muskle-ccb__title");
    var stack = root.querySelector(".muskle-ccb__title-stack");
    var subtitle = root.querySelector(".muskle-ccb__subtitle");
    if (title) title.setAttribute("aria-hidden", visible ? "false" : "true");
    if (stack) stack.setAttribute("aria-hidden", visible ? "false" : "true");
    if (subtitle) subtitle.setAttribute("aria-hidden", visible ? "false" : "true");
  }

  async function runLoop(root, signal) {
    try {
      while (!signal.aborted) {
        setSlide(root, 1);
        setTitleVisible(root, true);
        await wait(SLIDE_HOLD_MS, signal);

        setTitleVisible(root, false);
        await wait(TITLE_FADE_MS + TITLE_PAUSE_MS, signal);

        setSlide(root, 2);
        await wait(IMAGE_CROSSFADE_MS, signal);

        await wait(SLIDE_HOLD_MS, signal);

        setSlide(root, 1);
        await wait(IMAGE_CROSSFADE_MS, signal);

        setTitleVisible(root, true);
        await wait(TITLE_FADE_MS, signal);
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      throw err;
    }
  }

  function hasSlideLoop(root) {
    return root.hasAttribute("data-muskle-ccb-slide-loop");
  }

  function hasPromoWindow(root) {
    return (
      root.hasAttribute("data-muskle-ccb-promo-start") ||
      root.hasAttribute("data-muskle-ccb-promo-end")
    );
  }

  function parseIsoMs(iso) {
    if (!iso) return NaN;
    return Date.parse(iso);
  }

  function campaignIsActive(root) {
    if (!hasPromoWindow(root)) return hasSlideLoop(root);
    var now = Date.now();
    var startMs = parseIsoMs(root.getAttribute("data-muskle-ccb-promo-start"));
    var endMs = parseIsoMs(root.getAttribute("data-muskle-ccb-promo-end"));
    if (isNaN(startMs)) startMs = 0;
    if (isNaN(endMs)) endMs = Number.POSITIVE_INFINITY;
    return now >= startMs && now < endMs;
  }

  function setCampaignSlideDisplay(root, visible) {
    var slide2 = root.querySelector('[data-muskle-ccb-slide="2"]');
    if (!slide2) return;
    slide2.style.display = visible ? "" : "none";
  }

  function applyPromoWindow(root) {
    if (!hasPromoWindow(root)) return campaignIsActive(root);
    if (campaignIsActive(root)) {
      setCampaignSlideDisplay(root, true);
      root.setAttribute("data-muskle-ccb-slide-loop", "");
      return true;
    }
    setSlide(root, 1);
    setTitleVisible(root, true);
    root.removeAttribute("data-muskle-ccb-slide-loop");
    setCampaignSlideDisplay(root, false);
    return false;
  }

  function armPromoWindow(root) {
    if (!root || root.__muskleCcbWindowArmed || !hasPromoWindow(root)) return;
    root.__muskleCcbWindowArmed = true;

    var startMs = parseIsoMs(root.getAttribute("data-muskle-ccb-promo-start"));
    var endMs = parseIsoMs(root.getAttribute("data-muskle-ccb-promo-end"));
    var now = Date.now();
    var maxDelay = 2147483647;

    function restart() {
      applyPromoWindow(root);
      initBanner(root);
    }

    if (!isNaN(startMs) && startMs > now) {
      window.setTimeout(restart, Math.min(startMs - now + 50, maxDelay));
    }
    if (!isNaN(endMs) && endMs > now) {
      window.setTimeout(restart, Math.min(endMs - now + 50, maxDelay));
    }
  }

  function initBanner(root) {
    if (!root) return;

    if (root._muskleCcbAbort) {
      root._muskleCcbAbort.abort();
      root._muskleCcbAbort = null;
    }

    armPromoWindow(root);
    applyPromoWindow(root);

    setSlide(root, 1);
    setTitleVisible(root, true);
    root.classList.remove("muskle-ccb--reduced-motion");
    root.classList.remove("muskle-ccb--text-ready");

    if (prefersReducedMotion()) {
      root.classList.add("muskle-ccb--reduced-motion");
      root.classList.add("muskle-ccb--text-ready");
      if (hasPromoWindow(root) && campaignIsActive(root)) {
        setCampaignSlideDisplay(root, true);
        setSlide(root, 2);
        setTitleVisible(root, false);
      }
      root.dataset.muskleCcbInit = "1";
      return;
    }

    var controller = new AbortController();
    root._muskleCcbAbort = controller;
    root.dataset.muskleCcbInit = "1";

    whenImagesReady(root).then(function () {
      if (controller.signal.aborted) return;
      return wait(ENTRANCE_SETTLE_MS, controller.signal).then(function () {
        if (controller.signal.aborted) return;
        root.classList.add("muskle-ccb--text-ready");
        if (!hasSlideLoop(root)) return;
        return wait(ENTRANCE_TOTAL_MS, controller.signal).then(function () {
          if (controller.signal.aborted) return;
          return runLoop(root, controller.signal);
        });
      });
    });
  }

  function boot() {
    var nodes = document.querySelectorAll("[data-muskle-ccb-animated]");
    for (var i = 0; i < nodes.length; i++) initBanner(nodes[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    var scope = event.target;
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll("[data-muskle-ccb-animated]");
    for (var i = 0; i < nodes.length; i++) initBanner(nodes[i]);
  });
})();
